import { Fragment } from 'react';
import { Info } from 'lucide-react';

import type { KaiCoachSession, TranscriptSegment } from './kaiCoachContent';

import styles from './KaiCoachTranscript.module.css';

function classNames(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(' ');
}

/** A turn's text, with its highlighted phrases as <mark>. */
export function TranscriptSegments({ segments }: { segments: TranscriptSegment[] }) {
  return (
    <>
      {segments.map((segment, index) =>
        typeof segment === 'string' ? (
          <Fragment key={index}>{segment}</Fragment>
        ) : (
          <mark className={styles.mark} key={index}>
            {segment.mark}
          </mark>
        ),
      )}
    </>
  );
}

/** One example session: the transcript beside numbered notes on its moves. */
export function KaiCoachTranscript({ session }: { session: KaiCoachSession }) {
  return (
    <div className={styles.session}>
      <h3 className={styles.title}>{session.title}</h3>
      {session.designTarget ? (
        <p className={styles.flag}>
          <Info aria-hidden="true" className={styles.flagIcon} size={18} />
          <span>
            <strong>A design target, not a recorded session.</strong> {session.designTarget}
          </span>
        </p>
      ) : null}
      {session.intro ? <p className={styles.intro}>{session.intro}</p> : null}

      <div className={styles.layout}>
        <ol aria-label="Transcript" className={styles.turns}>
          {session.turns.map((turn, index) => (
            <li
              className={classNames(
                styles.turn,
                turn.speaker === 'student' ? styles.student : styles.coach,
              )}
              key={index}
            >
              <span aria-hidden="true" className={styles.avatar}>
                {turn.speaker === 'coach' ? 'K' : 'S'}
              </span>
              <div className={styles.bubble}>
                <span className={styles.who}>
                  {turn.label ?? (turn.speaker === 'coach' ? 'Kai Coach' : 'Student')}
                </span>
                <span className={styles.text}>
                  <TranscriptSegments segments={turn.segments} />
                </span>
                {turn.note ? <span className={styles.srOnly}> (note {turn.note})</span> : null}
              </div>
              {turn.note ? (
                <span aria-hidden="true" className={styles.marker}>
                  {turn.note}
                </span>
              ) : null}
            </li>
          ))}
        </ol>

        <ol aria-label="What to notice" className={styles.notes}>
          {session.notes.map((note, index) => (
            <li className={styles.note} key={note.title}>
              <span aria-hidden="true" className={styles.marker}>
                {index + 1}
              </span>
              <p className={styles.noteText}>
                <strong>{note.title}</strong> {note.body}
              </p>
            </li>
          ))}
        </ol>
      </div>

      <p className={styles.outcome}>{session.outcome}</p>
    </div>
  );
}
